import axios from 'axios';
import { api, extractErrorMessage, getActiveTenantId, MissingTenantError, requiresTenant } from './api';
import {
  ListQuery,
  ManyEnvelope,
  OneEnvelope,
  listQueryToParams,
} from '../types/api';

/**
 * Generic CRUD service bound to a backend resource path. Used by
 * `useCrudResource` and module-level pages to avoid one-off axios calls.
 *
 * Mirrors the response shapes emitted by `BaseController` in
 * `backend/app/src/core/base/`.
 */
/**
 * Per-request options. `tenantId` overrides the active-customer header for
 * this call: a number scopes to that tenant, `'*'` requests cross-tenant
 * scope over every tenant the user can access.
 */
export interface RequestOpts {
  tenantId?: number | '*';
  signal?: AbortSignal;
}

function optsToConfig(opts?: RequestOpts) {
  return {
    ...(opts?.tenantId !== undefined ? { headers: { 'X-Tenant-Id': String(opts.tenantId) } } : {}),
    signal: opts?.signal,
  };
}

export const LOOKUP_ERROR_EVENT = 'brandlight.lookup-error';
export class LookupReadError extends Error {}

export class CrudService<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  constructor(private readonly path: string) {}

  get requiresTenant(): boolean {
    return requiresTenant(this.path);
  }

  async list(query?: ListQuery, opts?: RequestOpts): Promise<ManyEnvelope<T>> {
    const res = await api.get<ManyEnvelope<T>>(this.path, {
      params: listQueryToParams(query),
      ...optsToConfig(opts),
    });
    return res.data;
  }

  /** Complete lookup/summary reads; paginated tables keep using list(). */
  async listAll(query?: ListQuery, opts?: RequestOpts): Promise<ManyEnvelope<T>> {
    const maxRows = 10_000;
    const maxPages = 100;
    const tenantId = opts?.tenantId ?? (this.requiresTenant ? getActiveTenantId() : null);
    const requestOpts = tenantId === null ? opts : { ...opts, tenantId };
    const sort = query?.sort ?? 'id';
    const stableSort = sort.split(',').some((field) => field.replace(/^-/, '') === 'id')
      ? sort
      : `${sort},id`;
    const rows: T[] = [];
    for (let page = 1; page <= maxPages; page++) {
      const result = await this.list({ ...query, sort: stableSort, page, pageSize: 100 }, requestOpts)
        .catch((error: unknown) => {
          if (error instanceof MissingTenantError || axios.isCancel(error)) throw error;
          throw new LookupReadError(extractErrorMessage(error));
        });
      if (result.page.total > maxRows || result.page.total_pages > maxPages) {
        throw new LookupReadError('Too many records for this view. Narrow the selection to at most 10,000 records.');
      }
      rows.push(...result.data);
      if (rows.length > maxRows) throw new LookupReadError('Lookup exceeds 10,000 records. Narrow the selection.');
      if (page >= result.page.total_pages) {
        const ids = new Set(rows.map((row) => (row as { id: unknown }).id));
        if (rows.length !== result.page.total || ids.size !== rows.length) {
          throw new LookupReadError('Records changed while loading. Refresh to load a complete list.');
        }
        return { data: rows, page: { page: 1, page_size: rows.length, total: rows.length, total_pages: 1 } };
      }
      if (result.data.length === 0) throw new LookupReadError('Incomplete list received. Refresh to retry.');
    }
    throw new LookupReadError('Lookup exceeds 100 pages. Narrow the selection.');
  }

  async get(id: number | string, include?: string[], opts?: RequestOpts): Promise<T> {
    const res = await api.get<OneEnvelope<T>>(`${this.path}/${id}`, {
      params: include?.length ? { include: include.join(',') } : undefined,
      ...optsToConfig(opts),
    });
    return res.data.data;
  }

  async create(payload: TCreate): Promise<T> {
    const res = await api.post<OneEnvelope<T>>(this.path, payload);
    return res.data.data;
  }

  async createBulk(payloads: TCreate[]): Promise<T[]> {
    if (payloads.length < 1 || payloads.length > 500) {
      throw new Error('Bulk attachment requires 1 to 500 records.');
    }
    const res = await api.post<{ data: T[] }>(`${this.path}/bulk`, payloads);
    return res.data.data;
  }

  async update(id: number | string, payload: TUpdate, opts?: RequestOpts): Promise<T> {
    const res = await api.put<OneEnvelope<T>>(`${this.path}/${id}`, payload, optsToConfig(opts));
    return res.data.data;
  }

  async patch(id: number | string, payload: TUpdate, opts?: RequestOpts): Promise<T> {
    const res = await api.patch<OneEnvelope<T>>(`${this.path}/${id}`, payload, optsToConfig(opts));
    return res.data.data;
  }

  /** Junction-only: update metadata, attaching only when the pair is absent. */
  async attachOrUpdate(id: number, payload: TUpdate): Promise<T> {
    try {
      return await this.patch(id, payload);
    } catch (error) {
      if (!axios.isAxiosError(error) || error.response?.status !== 404) throw error;
      return this.update(id, payload);
    }
  }

  /** Global catalog reads are open; writes require management permission. */
  get requiresManagementForWrites(): boolean {
    return [
      '/tenants', '/ai-models', '/ai-model-providers', '/ai-model-ai-providers',
      '/brands', '/organizations', '/categories', '/personas', '/products',
      '/domains', '/geo-locations', '/topics', '/search-phrases',
      '/visibility-weights', '/prompt-funnel-stages',
    ].some((path) => this.path === path || this.path.startsWith(`${path}/`));
  }

  async remove(id: number | string, opts?: RequestOpts): Promise<void> {
    await api.delete(`${this.path}/${id}`, optsToConfig(opts));
  }
}
